import { headers } from "next/headers"
import Link from "next/link"
import type { ComponentProps } from "react"
import { FaWhatsapp as WhatsappIcon } from "react-icons/fa"
import { Button } from "@/components/ui/button"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"
import { cn } from "@/utilities"
import { ProductDetailsAddCart } from "./add-cart"

interface ProductDetailsSummaryProps extends ComponentProps<"div"> {
  product: WooCommerceProduct
}

const stockStatusMap: Record<string, { label: string; className: string }> = {
  instock: { label: "Em estoque", className: "text-green-600" },
  outofstock: { label: "Fora de estoque", className: "text-red-600" },
  onbackorder: { label: "Em espera", className: "text-yellow-600" },
}

const separateCamelCase = (html: string): string => {
  return html.replace(/([a-z])([A-Z])/g, "$1;&nbsp;$2")
}

export const ProductDetailsSummary = async ({
  product,
  className,
  ...props
}: ProductDetailsSummaryProps) => {
  const priceNumber = Number(product.price || product.regular_price || 0)
  const formattedPrice = priceNumber.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  })

  const hasStock = product.stock_status === "instock"

  const stockStatus = hasStock
    ? stockStatusMap[product.stock_status]
    : {
        label: "Indisponível",
        className: "text-gray-600",
      }

  const h = await headers()

  const host = h.get("host")
  const protocol = h.get("x-forwarded-proto") ?? "https"

  const PRODUCT_URL = `${protocol}://${host}/produto/${product.slug}`
  const WHATSAPP_MESSAGE = `Olá, gostaria de comprar:%0A%0A*${encodeURIComponent(product.name)}*%0A*Preço:* ${encodeURIComponent(formattedPrice)}%0A*URL:* ${encodeURIComponent(PRODUCT_URL)}%0A%0AObrigado!`
  const WHATSAPP_URL = `https://web.whatsapp.com/send?phone=${process.env.NEXT_PUBLIC_WHATSAPP_NUMBER}&text=${WHATSAPP_MESSAGE}&app_absent=0`

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      <span className='font-1st font-bold text-3xl text-black'>
        {formattedPrice}
      </span>

      <div className='flex flex-wrap items-center gap-4 text-sm'>
        <span>
          Vendido Por:{" "}
          <Link
            className='font-1st text-blue-600 text-xl uppercase'
            href={`/lojas/${product.store?.url.split("/")[4]}`}
          >
            {product.store?.shop_name || "—"}
          </Link>
        </span>
        <span>
          Status:{" "}
          <strong className={stockStatus.className}>{stockStatus.label}</strong>
        </span>
      </div>

      <hr className='border-gray-200' />

      <div className='text-gray-700 text-sm leading-6'>
        {product.short_description ? (
          <div
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <woo commerce returns html descriptions>
            dangerouslySetInnerHTML={{
              __html: separateCamelCase(product.short_description),
            }}
          />
        ) : (
          <p className='line-clamp-3'>
            {product.description?.replace(/<[^>]*>/g, "")}
          </p>
        )}
      </div>

      <hr className='border-gray-200' />

      <div className='flex flex-wrap gap-4'>
        <ProductDetailsAddCart
          className='w-full'
          disabled={!hasStock}
          productId={product.id}
        />

        <Button
          asChild
          className='w-full flex-1 bg-green-500 hover:bg-green-600 md:max-w-100'
          size='lg'
        >
          <a href={WHATSAPP_URL} rel='noopener noreferrer' target='_blank'>
            <WhatsappIcon />
            Se preferir compre pelo whatsapp
          </a>
        </Button>
      </div>

      <hr className='border-gray-200' />

      {/* Categorias */}
      <p className='text-gray-700 text-sm'>
        Categorias:{" "}
        {product.categories?.map((cat, i) => (
          <span key={cat.id}>
            <Link
              className='text-blue-600 hover:underline'
              href={`/categorias/${cat.slug}`}
              prefetch
            >
              {cat.name}
            </Link>
            {i < product.categories.length - 1 && ", "}
          </span>
        ))}
      </p>
    </div>
  )
}
